const plugin = require('tailwindcss/plugin')

const remBase = 16
const calculateRem = (pixels) => pixels / remBase + 'rem'

module.exports = {
    future: {
        hoverOnlyWhenSupported: true,
    },
    content: [
        './components/**/*.vue',
        './layouts/**/*.vue',
        './pages/**/*.vue',
        './config/**/*.js',
    ],
    safelist: [
        {
            pattern: /^col-.*/,
            variants: ['sm', 'md', 'lg', 'xl', '2xl'],
        },
        {
            pattern: /^bg-.*/,
            variants: ['sm', 'md', 'lg', 'xl', '2xl'],
        },
    ],
    theme: {
        colors: {
            transparent: 'transparent',
            current: 'currentColor',
            white: '#FFFFFF', // $brand-white
            black: '#333333', // $brand-black
            utility: {
                info: {
                    darker: '#29657E',
                    dark: '#3B7D99',
                    DEFAULT: '#4D94B2',
                    light: '#ACD2E2',
                    lighter: '#F2F5F6',
                },
                success: {
                    darker: '#205642',
                    dark: '#2F745D',
                    DEFAULT: '#469377',
                    light: '#C7DFD5',
                    lighter: '#EFF4ED',
                },
                warning: {
                    darker: '#925423',
                    dark: '#B87036',
                    DEFAULT: '#D58545',
                    light: '#E9BE9C',
                    lighter: '#FEF6EE',
                },
                error: {
                    darker: '#831923',
                    dark: '#9E202C',
                    DEFAULT: '#BD313E',
                    light: '#E5C1C5',
                    lighter: '#FDF7F7',
                },
            },
            red: {
                DEFAULT: '#AF3D42',
                light: '#FBEAEB',
            },
            success: {
                DEFAULT: '#5EA789',
                light: '#f3f8f4',
            }, // $brand-success
            danger: {
                DEFAULT: '#FF7777',
                light: '#fbebec',
            }, // $brand-danger
            notify: '#FFCA3F', // $brand-notify
            warning: {
                DEFAULT: '#F5A623',
                light: '#FFF5EE',
            }, // $brand-warning
            highlight: '#f57723', // $brand-highlight
            information: {
                DEFAULT: '#3160AD',
                light: '#EFF4FC',
            }, // $brand-information
            teal: {
                300: '#9EC69E',
                500: '#628885',
                800: '#5A7E7B',
            },
            primary: {
                100: '#eefaf1',
                300: '#7fb98e',
                500: '#5EA789',
                700: '#58a16c',
            },
            grey: {
                200: '#FCFCFC', // $brand-grey-lightest
                300: '#F7F7F7', // $brand-grey-lighter
                400: '#EBEBEB', // $brand-grey-light
                500: '#ABABAB', // $brand-grey
                600: '#999999', // $brand-grey-dark
                700: '#666666', // $brand-grey-darker
                800: '#333333',
            },
            // 2021 colors (slowly depricated)
            infogrey: '#F2F6F6',
            salmon: '#E88E8E',
            // 2022 colors
            pine: '#42594F',
            pink: '#EBBAA3',
            terra: '#984F35',
            mustard: '#DDB254',
            creme: '#F5F3EE',
            coffee: '#E0D9D9',
            beige: '#F8F6F2',
            gin: '#DBE9E3',
            sale: '#C18A5A',
            blue: '#4D94B2',
            sky: '#ACD2E2',
            mint: '#CAD1CB',
            stone: '#DCDCDC',
            snow: '#F8F8F7',
            yellow: {
                100: '#FFFBEF',
                200: '#F9D35A',
                300: '#E2B700',
                400: '#DBAC43',
            },
            gold: {
                DEFAULT: '#EEBF23',
                light: '#FFF4CE',
            },
            orange: {
                DEFAULT: '#D48545',
                light: '#FDF6EF',
            },
            olive: {
                DEFAULT: '#638759',
                light: '#D4E8CC',
            },
            cerulean: {
                DEFAULT: '#6992AC',
                light: '#F2F5F6',
            },
        },
        fontFamily: {
            sans: [
                'proxima-nova',
                'Segoe UI',
                'Helvetica',
                'Arial',
                'sans-serif',
            ],
            serif: ['Lora', 'serif'],
        },
        fontSize: {
            base: calculateRem(16),

            4: calculateRem(4), // 0.25rem ($size-9)
            8: calculateRem(8), // 0.5rem ($size-8)
            10: calculateRem(10), // 0.625rem
            11: calculateRem(11), // 0.6875rem
            12: calculateRem(12), // 0.75rem ($size-7)
            13: calculateRem(13), // 0.8125rem
            14: calculateRem(14), // 0.875rem
            15: calculateRem(15),
            16: calculateRem(16), // 1rem ($size-6)
            18: calculateRem(18), // 1.125rem
            20: calculateRem(20), // 1.25rem ($size-5)
            24: calculateRem(24), // 1.5rem ($size-4)
            27: calculateRem(27), // 1.6875rem
            32: calculateRem(32), // 2rem ($size-3)
            36: calculateRem(36), // 2.25rem ($size-3)
            40: calculateRem(40), // 2.5rem ($size-2)
            48: calculateRem(48), // 3rem ($size-1)
            54: calculateRem(54), // 3.375rem ($size-0)
            60: calculateRem(60), // 3.75rem ($size-0)
        },
        fontWeight: {
            100: 100,
            200: 200,
            300: 300,
            400: 400,
            500: 500,
            600: 600,
            700: 700,
            800: 800,
            900: 900,
        },
        extend: {
            // 1 = 0.25rem --> always divide by 4.
            spacing: {
                7: '1.75rem',
                9: '2.25rem',
                15: '3.75rem',
            },

            // Add some legacy breakpoints for easier upgrading.
            screens: {
                touch: { max: '1024px' },
                mobile: { max: '767px' },
                xsMobile: { max: '480px' },
                xxsMobile: { max: '360px' },
            },
            boxShadow: {
                dropdown: '0 0 8px rgba(51, 51, 51, 0.20)',
            },
            animation: {
                'ping-once': 'ping 0.5s cubic-bezier(0, 0, 0.2, 1) reverse',
                'ping-slow': 'ping 1.5s cubic-bezier(0, 0, 0.2, 1) 3',
                'bounce-once': 'bounce 1s 3',
            },
        },
        container: {
            center: true,
            padding: {
                DEFAULT: '1rem',
            },
        },
    },

    plugins: [
        require('@tailwindcss/typography'),
        require('./assets/css/tailwind/buttons-plugin.js'),
        plugin(function ({ addUtilities }) {
            const newUtilities = {
                '.abs-center': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    margin: 'auto',
                },
            }

            addUtilities(newUtilities)
        }),
        plugin(function ({ addVariant }) {
            addVariant('child', '& > *')
        }),
    ],
}
